import React, { useMemo, useState } from "react";
import { Alert, Button, Skeleton, Stack, Text } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useResponsive } from "../../../context/ResponsiveContext";
import { useSalon } from "../../../context/SalonContext";
import usePermissions from "../../../hooks/usePermissions";
import { Employee } from "../../../types/salon";
import { SalonContextType } from "../../../types/context";
import {
  SettingsEditButton,
  SettingsPageHeader,
  SettingsPageLayout,
  SettingsTabShell,
} from "../common";
import NotificationPreferenceCards from "./NotificationPreferenceCards";
import NotificationPreferencesMatrix, {
  StaffProfileDetails,
} from "./NotificationPreferencesMatrix";
import { buildPreferenceRows } from "./preferenceRows";
import { useNotificationPreferences } from "./useNotificationPreferences";

const SUBTITLE = "Choose which notifications each staff member receives.";

/**
 * Settings -> Notifications. Owners (and anyone granted the
 * settingsNotifications permission) choose, per staff member, which
 * notifications they receive; staff limited to "own" see only themselves.
 * Follows the Settings read/edit pattern (see GiftCardSettings): values show as
 * text until Edit in the header, then controls with a Cancel / Save footer.
 * The backend decides who is listed and who is editable — this page renders
 * what it returns: a matrix on desktop, one card per person on mobile.
 */
const NotificationSettings: React.FC = () => {
  const { isMobile } = useResponsive();
  const { employees } = useSalon() as SalonContextType;
  const permissions = usePermissions();
  const hasAccess = permissions.getScope("settingsNotifications") !== "none";
  const {
    categories,
    staff,
    isLoading,
    isSaving,
    loadError,
    setPreference,
    setEmailOptIn,
    save,
    discard,
    retry,
  } = useNotificationPreferences({ enabled: hasAccess });
  const [editing, setEditing] = useState(false);

  const rows = useMemo(() => buildPreferenceRows(categories), [categories]);

  // Photos and titles aren't part of the preferences API; reuse the salon's
  // staff list.
  const staffDetails = useMemo(
    () =>
      Object.fromEntries(
        ((employees as Employee[] | null) ?? []).map(
          (employee): [string, StaffProfileDetails] => [
            employee.id,
            {
              photoUrl: employee.staff_photo_url ?? employee.avatar_url,
              title: employee.title,
            },
          ],
        ),
      ),
    [employees],
  );

  const startEdit = () => {
    discard();
    setEditing(true);
  };

  const cancelEdit = () => {
    discard();
    setEditing(false);
  };

  const handleSave = async () => {
    // On failure the hook has already shown the error; stay in edit mode so
    // nothing typed is lost.
    if (await save()) setEditing(false);
  };

  const canEditAnyone = staff.some((entry) => entry.editable);

  if (!hasAccess) {
    return (
      <SettingsPageLayout
        header={
          <SettingsPageHeader title="Notifications" subtitle={SUBTITLE} />
        }
      >
        <Alert icon={<IconAlertCircle size={16} />} color="yellow">
          You don't have access to notification settings.
        </Alert>
      </SettingsPageLayout>
    );
  }

  const header = (
    <SettingsPageHeader
      title="Notifications"
      subtitle={SUBTITLE}
      actions={
        !editing && !isLoading && !loadError && canEditAnyone ? (
          <SettingsEditButton onClick={startEdit} />
        ) : undefined
      }
    />
  );

  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <Stack gap="sm" data-testid="notification-settings-loading">
        {[0, 1, 2, 3].map((index) => (
          <Skeleton key={index} height={isMobile ? 120 : 56} radius="md" />
        ))}
      </Stack>
    );
  } else if (loadError) {
    body = (
      <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
        <Stack gap="sm" align="flex-start">
          <Text size="sm">{loadError}</Text>
          <Button size="xs" variant="light" color="red" onClick={retry}>
            Try again
          </Button>
        </Stack>
      </Alert>
    );
  } else if (staff.length === 0) {
    body = (
      <Text c="dimmed" ta="center" py="xl">
        No active staff to configure yet.
      </Text>
    );
  } else {
    const Layout = isMobile
      ? NotificationPreferenceCards
      : NotificationPreferencesMatrix;
    body = (
      <SettingsTabShell
        editing={editing}
        saving={isSaving}
        hideEditButton
        onEdit={startEdit}
        onCancel={cancelEdit}
        onSave={handleSave}
      >
        <Layout
          rows={rows}
          staff={staff}
          staffDetails={staffDetails}
          editing={editing}
          onPreferenceChange={setPreference}
          onEmailOptInChange={setEmailOptIn}
        />
      </SettingsTabShell>
    );
  }

  return <SettingsPageLayout header={header}>{body}</SettingsPageLayout>;
};

export default NotificationSettings;
